import { useCallback, useRef, useState } from 'react';
import { useEventListener } from './useEventListener';

export const useContainerFocus = (
  containerRef: React.RefObject<HTMLElement>,
  initiallyFocus: boolean = false,
  onFocusChange?: (isFocused: boolean) => void,
) => {
  const [isFocused, setIsFocused] = useState(initiallyFocus);
  const ignoreNextClick = useRef(false);

  const updateFocus = useCallback(
    (val: boolean) => {
      setIsFocused(val);
      if (onFocusChange) {
        onFocusChange(val);
      }
    },
    [onFocusChange],
  );

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }
      if (!containerRef.current) {
        return;
      }
      const el = document.elementFromPoint(event.clientX, event.clientY);
      const clickedInside = el && containerRef.current.contains(el);
      updateFocus(!!clickedInside);
    },
    [containerRef, updateFocus],
  );

  useEventListener('click', handleClick);

  const setFocus = useCallback(
    (val: boolean) => {
      if (val) {
        ignoreNextClick.current = true;
      }
      updateFocus(val);
    },
    [updateFocus],
  );

  return [isFocused, setFocus] as const;
};
